import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { PROJECT_FILES_QUERY } from '../../graphql/queries';
import { formatDuration } from '../../utils/video360';
import styles from './WalkthroughsMenu.module.css';
import Icon from '../../components/common/Icon';

const MENU_VIDEOS = 3;

interface VideoFile {
  id: string;
  originalName: string;
  fileSize: number;
  status: string;
  createdAt: string;
  thumbnailUrl?: string | null;
  videoDuration?: number | null;
}

interface WalkthroughsMenuProps {
  projectId: string;
  matterportUrl?: string | null;
  matterportAllowed: boolean;
  canEditMatterport: boolean;
  videosAllowed: boolean;
  onOpenMatterport: () => void;
  onEditMatterport: () => void;
}

const formatSize = (bytes: number) => {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  return `${Math.max(1, Math.round(bytes / 1024 ** 2))} MB`;
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

// Every "walk through this site" experience in one header entry: the project's
// Matterport tour (external, one link) and its 360 videos (uploaded files).
const WalkthroughsMenu = ({
  projectId, matterportUrl, matterportAllowed, canEditMatterport, videosAllowed, onOpenMatterport, onEditMatterport,
}: WalkthroughsMenuProps) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const { data } = useQuery(PROJECT_FILES_QUERY, {
    variables: { projectId, type: 'video360', page: 1, limit: MENU_VIDEOS },
    skip: !videosAllowed,
    fetchPolicy: 'cache-and-network',
  });
  const videos: VideoFile[] = data?.projectFiles?.files ?? [];
  const videoTotal: number = data?.projectFiles?.total ?? 0;

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const showMatterport = matterportAllowed && (!!matterportUrl || canEditMatterport);
  if (!showMatterport && !videosAllowed) return null;

  const hasTour = matterportAllowed && !!matterportUrl;
  const latest = videos[0];
  // With something inside, the button previews it: latest video (else the
  // tour icon) plus a line saying what the menu holds.
  const summary = [
    hasTour ? '1 tour' : null,
    videoTotal > 0 ? `${videoTotal} video${videoTotal === 1 ? '' : 's'}` : null,
  ].filter(Boolean).join(' · ');
  const go = (path: string) => { setOpen(false); navigate(path); };
  const videosPath = `/projects/${projectId}/video360`;

  return (
    <div ref={wrapRef} className={styles.wrap}>
      <button
        type="button"
        className={`${styles.trigger} ${summary ? styles.triggerRich : ''} ${open ? styles.triggerOpen : ''}`}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={summary ? `Walkthroughs: ${summary}` : undefined}
      >
        {summary ? (
          <>
            {latest ? (
              <span className={styles.preview}>
                {latest.thumbnailUrl && <img src={latest.thumbnailUrl} alt="" />}
                <Icon name="play_arrow" className={styles.previewPlay} />
              </span>
            ) : (
              <span className={`${styles.preview} ${styles.previewTour}`}>
                <Icon name="view_in_ar" />
              </span>
            )}
            <span className={styles.triggerText}>
              <b>Walkthroughs</b>
              <small>{latest || !hasTour ? summary : 'Matterport tour'}</small>
            </span>
          </>
        ) : (
          <>
            <Icon name="vrpano" className="text-sm" />
            Walkthroughs
          </>
        )}
        <Icon name={open ? 'expand_less' : 'expand_more'} className="text-base" />
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          {(hasTour || videoTotal > 0) && (
            <div className={styles.strip}>
              <p className={styles.label}>In this project</p>
              <div className={styles.chips}>
                {hasTour && <span className={`${styles.chip} ${styles.chipTour}`}>1 Matterport tour</span>}
                {videoTotal > 0 && (
                  <span className={`${styles.chip} ${styles.chipVideo}`}>{videoTotal} 360 video{videoTotal === 1 ? '' : 's'}</span>
                )}
              </div>
            </div>
          )}

          {showMatterport && (
            <button
              type="button"
              role="menuitem"
              className={styles.item}
              onClick={() => { setOpen(false); if (matterportUrl) onOpenMatterport(); else onEditMatterport(); }}
            >
              <span className={`${styles.tile} ${styles.tileTour}`}>
                <Icon name="view_in_ar" />
              </span>
              <span className={styles.text}>
                <b>{matterportUrl ? 'Matterport tour' : 'Add Matterport tour'}</b>
                <small>{matterportUrl ? 'Opens the Matterport model · external' : 'Paste a Matterport share link'}</small>
              </span>
              <Icon name={matterportUrl ? 'open_in_new' : 'add_link'} className={styles.trail} />
            </button>
          )}

          {videosAllowed && (
            <>
              {showMatterport && <div className={styles.rule} />}
              <p className={styles.groupLabel}>360 videos</p>
              {videos.length === 0 ? (
                <p className={styles.empty}>No 360 videos yet.</p>
              ) : videos.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="menuitem"
                  className={styles.item}
                  onClick={() => go(v.status === 'ready' ? `/projects/${projectId}/viewer-360/${v.id}` : videosPath)}
                >
                  <span className={styles.thumb}>
                    {v.thumbnailUrl
                      ? <img src={v.thumbnailUrl} alt="" />
                      : <Icon name="play_arrow" />}
                  </span>
                  <span className={styles.text}>
                    <b title={v.originalName}>{v.originalName}</b>
                    <small>
                      {formatDate(v.createdAt)} · {v.videoDuration != null ? formatDuration(v.videoDuration) : formatSize(v.fileSize)}
                      {v.status !== 'ready' && ` · ${v.status}`}
                    </small>
                  </span>
                </button>
              ))}
              <div className={styles.rule} />
              <div className={styles.footer}>
                <button type="button" className={styles.link} onClick={() => go(videosPath)}>
                  All 360 videos
                  <Icon name="chevron_right" />
                </button>
                <button type="button" className={styles.link} onClick={() => go(`${videosPath}?upload=1`)}>
                  <Icon name="add" />
                  Upload 360 video
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default WalkthroughsMenu;
